import type {
  APIGatewayProxyCallbackV2,
  APIGatewayProxyEventV2WithJWTAuthorizer,
  APIGatewayProxyResultV2,
} from "aws-lambda"
import { getErrorMessage } from "./Utils.Errors"
import type { ObjectNotPromise } from "./Utils"

export enum HttpStatusCodes {
  Success = 200,
  BadRequest = 400,
  // 401 means: not authenticated
  Unauthendicated = 401,
  // 403 means: authenticated, but not authorized
  Unauthorized = 403,
  NotFound = 404,
  Conflict = 409,
  InternalServerError = 500,
}
export function getSuccessResponse<TArg extends object>(
  payload: ObjectNotPromise<TArg> | string | void[]
): APIGatewayProxyResultV2 {
  return getResponse(HttpStatusCodes.Success, JSON.stringify(payload))
}

export const getErrorResponse = (message: string): APIGatewayProxyResultV2 => {
  console.error("> getErrorResponse:", message)
  const msgLc = message.toLowerCase()
  if (msgLc.includes("access denied")) {
    return getResponse(
      HttpStatusCodes.Unauthorized,
      JSON.stringify({ error: message })
    )
  }
  if (msgLc.includes("nicht gefunden") || msgLc.includes("not found")) {
    return getResponse(
      HttpStatusCodes.NotFound,
      JSON.stringify({ error: message })
    )
  }
  return getResponse(
    HttpStatusCodes.InternalServerError,
    JSON.stringify({ error: message })
  )
}

export const getResponse = (
  statusCode: number,
  body: string | object
): APIGatewayProxyResultV2 => {
  if (typeof body === "object") {
    body = JSON.stringify(body)
  }
  return {
    statusCode: statusCode,
    headers: {
      "Content-Type": "application/json",
    },
    body: body,
  }
}

export const sendSuccessResponse = (
  payload: any,
  callback: APIGatewayProxyCallbackV2
) => {
  sendResponse(HttpStatusCodes.Success, JSON.stringify(payload), callback)
}

export const sendErrorResponse = (
  message: string,
  callback: APIGatewayProxyCallbackV2
) => {
  console.error(message)
  sendResponse(
    HttpStatusCodes.InternalServerError,
    JSON.stringify({ error: message }),
    callback
  )
}

const sendResponse = (
  statusCode: number,
  body: string,
  callback: APIGatewayProxyCallbackV2
) => {
  const response = {
    statusCode: statusCode,
    // headers: {
    //     'x-custom-header': 'My Header Value',
    // },
    body: body,
  }
  callback(null, response)
}

type SyncCatchErrorCallback = () => APIGatewayProxyResultV2
type AsyncCatchErrorCallback = () => Promise<APIGatewayProxyResultV2>

export function catchError(
  callback: SyncCatchErrorCallback
): APIGatewayProxyResultV2 {
  try {
    return callback()
  } catch (err: unknown) {
    return getErrorResponse(getErrorMessage(err))
  }
}

export async function catchErrorAsync(
  callback: AsyncCatchErrorCallback
): Promise<APIGatewayProxyResultV2> {
  try {
    return await callback()
  } catch (err: unknown) {
    return Promise.reject(getErrorResponse(getErrorMessage(err)))
  }
}

/**
 * Extracts the current user id from the jwt token's claims.
 * @todo verify jwt signature
 *
 * @param event
 * @returns
 */
export const extractCurrentUserId = (
  event: APIGatewayProxyEventV2WithJWTAuthorizer
) => {
  console.debug("> extractCurrentUserId")
  const userId = event.requestContext.authorizer.jwt.claims["sub"] as string
  console.debug("extractCurrentUserId:", userId)
  return userId
}
